import { collection, doc, setDoc, updateDoc, Timestamp, getDocs, query, where } from 'firebase/firestore';
import { db } from '../config/firebase';
import { AdapterFactory } from '../adapters';
import { AIResponse, AuditRun, AuditConfig, ExtractedClaim } from '../types';
import { AISystem } from '../config/ai';
import { fillPromptTemplate } from './promptLibrary';

/**
 * Response processor - extracts claims, sentiment, and mentions from AI responses
 */
class ResponseProcessor {
  /**
   * Extract discrete claims from AI response text
   * Uses simple heuristics for MVP - can be enhanced with LLM-assisted extraction later
   */
  extractClaims(responseText: string, _brandName: string): ExtractedClaim[] {
    const claims: ExtractedClaim[] = [];
    const sentences = responseText.split(/[.!?]+/).filter(s => s.trim().length > 0);

    sentences.forEach((sentence) => {
      const text = sentence.trim();
      if (text.length < 10) return; // Skip very short fragments

      // Detect claim type based on keywords
      let type: ExtractedClaim['type'] = 'general';
      if (/\$|£|€|price|pricing|cost|fee/.test(text.toLowerCase())) {
        type = 'pricing';
      } else if (/feature|capability|function|tool|platform/.test(text.toLowerCase())) {
        type = 'feature';
      } else if (/located|based|headquarters|office/.test(text.toLowerCase())) {
        type = 'location';
      } else if (/founded|established|started|launched|\d{4}/.test(text)) {
        type = 'date';
      }

      claims.push({
        text,
        type,
        isAccurate: null, // Will be verified against source of truth later
        severity: null,
        sourceFragment: text,
      });
    });

    return claims;
  }

  /**
   * Analyze sentiment of the response
   * Simple keyword-based analysis for MVP
   */
  analyzeSentiment(responseText: string): { sentiment: 'positive' | 'neutral' | 'negative'; score: number } {
    const text = responseText.toLowerCase();

    // Positive indicators
    const positiveWords = ['excellent', 'great', 'best', 'leading', 'top', 'powerful', 'innovative', 'reliable', 'trusted', 'recommended'];
    const positiveCount = positiveWords.filter(word => text.includes(word)).length;

    // Negative indicators
    const negativeWords = ['poor', 'limited', 'lacking', 'weak', 'problematic', 'difficult', 'expensive', 'outdated', 'not recommended'];
    const negativeCount = negativeWords.filter(word => text.includes(word)).length;

    let sentiment: 'positive' | 'neutral' | 'negative';
    let score: number;

    if (positiveCount > negativeCount) {
      sentiment = 'positive';
      score = Math.min(100, 60 + (positiveCount * 10));
    } else if (negativeCount > positiveCount) {
      sentiment = 'negative';
      score = Math.max(0, 40 - (negativeCount * 10));
    } else {
      sentiment = 'neutral';
      score = 50;
    }

    return { sentiment, score };
  }

  /**
   * Check if brand is mentioned in the response
   */
  isBrandMentioned(responseText: string, brandName: string): boolean {
    return responseText.toLowerCase().includes(brandName.toLowerCase());
  }

  /**
   * Check if brand is mentioned in a recommendation context
   */
  isRecommendationContext(responseText: string, brandName: string): boolean {
    if (!this.isBrandMentioned(responseText, brandName)) return false;

    const text = responseText.toLowerCase();
    const recommendationKeywords = ['recommend', 'suggest', 'consider', 'choose', 'best option', 'good choice', 'try'];

    return recommendationKeywords.some(keyword => text.includes(keyword));
  }
}

/**
 * Audit Engine - Orchestrates audit runs
 */
export class AuditEngine {
  private processor = new ResponseProcessor();

  /**
   * Run a complete audit
   */
  async runAudit(
    userId: string,
    config: AuditConfig,
    prompts: Array<{ id: string; template: string; variables: string[] }>,
    variables: Record<string, string>
  ): Promise<string> {
    // Create audit run record
    const auditRunRef = doc(collection(db, 'auditRuns'));
    const auditRun: AuditRun = {
      id: auditRunRef.id,
      userId,
      configId: config.id,
      status: 'running',
      startedAt: Timestamp.now(),
      totalQueries: prompts.length * config.aiSystems.length * 3, // 3 runs per prompt per system
      completedQueries: 0,
      visibilityScore: 0,
    };

    await setDoc(auditRunRef, auditRun);

    try {
      // Run all prompts across all AI systems
      for (const promptConfig of prompts) {
        const filledPrompt = fillPromptTemplate(promptConfig.template, variables);

        for (const aiSystem of config.aiSystems) {
          await this.runPromptWithRetries(
            auditRunRef.id,
            promptConfig.id,
            filledPrompt,
            aiSystem,
            config.brandName
          );

          // Update progress
          const completedQueries = auditRun.completedQueries + 3;
          await updateDoc(auditRunRef, { completedQueries });
        }
      }

      // Mark as completed
      await updateDoc(auditRunRef, {
        status: 'completed',
        completedAt: Timestamp.now(),
      });

      return auditRunRef.id;
    } catch (error) {
      console.error('Audit failed:', error);
      await updateDoc(auditRunRef, {
        status: 'failed',
        completedAt: Timestamp.now(),
      });
      throw error;
    }
  }

  /**
   * Run a single prompt with retry logic (3x for median scoring)
   */
  private async runPromptWithRetries(
    auditRunId: string,
    promptId: string,
    promptText: string,
    aiSystem: AISystem,
    brandName: string
  ): Promise<void> {
    const adapter = AdapterFactory.getAdapter(aiSystem);

    if (!adapter.isConfigured()) {
      console.warn(`Skipping ${aiSystem} - not configured`);
      return;
    }

    // Run 3 times for median scoring
    for (let runNumber = 1; runNumber <= 3; runNumber++) {
      try {
        const response = await adapter.query(promptText);

        // Process the response
        const extractedClaims = this.processor.extractClaims(response.rawText, brandName);
        const { sentiment, score: sentimentScore } = this.processor.analyzeSentiment(response.rawText);
        const mentioned = this.processor.isBrandMentioned(response.rawText, brandName);
        const recommendationContext = this.processor.isRecommendationContext(response.rawText, brandName);

        // Save response
        const responseRef = doc(collection(db, 'aiResponses'));
        const aiResponse: AIResponse = {
          id: responseRef.id,
          auditRunId,
          promptId,
          promptText,
          aiSystem,
          modelVersion: response.modelVersion,
          rawResponse: response.rawText,
          timestamp: Timestamp.fromDate(response.timestamp),
          extractedClaims,
          sentiment,
          sentimentScore,
          mentioned,
          recommendationContext,
          runNumber,
        };

        await setDoc(responseRef, aiResponse);
      } catch (error) {
        console.error(`Error running prompt on ${aiSystem} (attempt ${runNumber}):`, error);
        // Continue with next attempt
      }
    }
  }

  /**
   * Get all responses for an audit run
   */
  async getAuditResponses(auditRunId: string): Promise<AIResponse[]> {
    const q = query(
      collection(db, 'aiResponses'),
      where('auditRunId', '==', auditRunId)
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => doc.data() as AIResponse);
  }
}

export const auditEngine = new AuditEngine();
